(() => {
  if (window.OBStockFirst) return;
  let initialController;

  // Page the concatenation of two Shopify-sorted result sets, not just the
  // current page. This keeps sold-out items behind ALL available products.
  async function reorder(html, address, options = {}) {
    const selector = options.gridSelector || '#collection-products';
    const grid = html.querySelector(selector);
    const meta = html.querySelector('[data-ob-stock-page]');
    if (!grid || !meta) return;
    const base = new URL(address, location.origin);
    if (base.origin !== location.origin) throw new Error('Invalid collection URL');
    const selected = [...new Set(base.searchParams.getAll('filter.v.availability').filter(value => value === '0' || value === '1'))];
    // Preserve the customer's explicit Shopify availability filter.
    if (selected.length === 1) return;
    const page = Number(meta.dataset.page), size = Number(meta.dataset.pageSize), total = Number(meta.dataset.total);
    if (![page, size, total].every(Number.isSafeInteger) || page < 1 || size < 1 || total < 0) throw new Error('Invalid collection page');
    if (!total) return;
    const existing = [...grid.children].filter(node => node.hasAttribute('data-ob-product-id'));
    if (total <= size && existing.length === total) {
      grid.replaceChildren(...existing.filter(node => node.dataset.obAvailable === 'true'), ...existing.filter(node => node.dataset.obAvailable !== 'true'));
      return;
    }
    if (meta.dataset.sectionId) base.searchParams.set('section_id', meta.dataset.sectionId);
    const signal = options.signal ? AbortSignal.any([options.signal, AbortSignal.timeout(15000)]) : AbortSignal.timeout(15000);
    const requests = new Map();

    function read(available, pageNumber) {
      const key = `${available}:${pageNumber}`;
      if (!requests.has(key)) requests.set(key, (async () => {
        const url = new URL(base);
        url.searchParams.set('filter.v.availability', String(available));
        url.searchParams.set('page', String(pageNumber));
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal });
        if (!response.ok) throw new Error('Stock order request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = doc.querySelector('[data-ob-stock-page]'), source = doc.querySelector(selector);
        if (!data || !source || Number(data.dataset.pageSize) !== size) throw new Error('Incomplete stock order response');
        const count = Number(data.dataset.total);
        if (!Number.isSafeInteger(count) || count < 0 || count > total) throw new Error('Collection changed during loading');
        const cards = [...source.children].filter(node => node.hasAttribute('data-ob-product-id'));
        if (available && cards.some(node => node.dataset.obAvailable !== 'true')) throw new Error('Availability filter was not applied');
        return { count, cards };
      })());
      return requests.get(key);
    }

    const availableCount = (await read(1, 1)).count;
    const start = (page - 1) * size, end = Math.min(start + size, total);
    async function range(available, from, to, count) {
      if (to <= from) return [];
      // Shopify's unavailable filter also matches partially sold-out products.
      // Skip those overlaps so every product occupies exactly one position.
      if (!available) {
        const soldOut = [];
        let filteredCount;
        for (let number = 1; ; number++) {
          const result = await read(0, number);
          if (filteredCount === undefined) filteredCount = result.count;
          if (result.count !== filteredCount || result.count < count) throw new Error('Collection changed during loading');
          soldOut.push(...result.cards.filter(node => node.dataset.obAvailable === 'false'));
          if (soldOut.length >= to || number * size >= filteredCount) break;
        }
        return soldOut.slice(from, to);
      }
      const first = Math.floor(from / size) + 1, last = Math.floor((to - 1) / size) + 1;
      const pages = await Promise.all(Array.from({ length: last - first + 1 }, (_, i) => read(available, first + i)));
      if (pages.some(result => result.count !== count)) throw new Error('Collection changed during loading');
      return pages.flatMap(result => result.cards).slice(from % size, from % size + to - from);
    }
    const groups = await Promise.all([
      range(1, start, Math.min(end, availableCount), availableCount),
      range(0, Math.max(0, start - availableCount), Math.max(0, end - availableCount), total - availableCount)
    ]);
    const cards = groups.flat();
    if (cards.length !== Math.max(0, end - start) || new Set(cards.map(node => node.dataset.obProductId)).size !== cards.length) throw new Error('Incomplete stock ordering');
    signal.throwIfAborted();
    grid.replaceChildren(...cards);
  }

  function cancelInitial() { initialController?.abort(); }
  async function init() {
    const grid = document.querySelector('#collection-products');
    if (!grid || !document.querySelector('[data-ob-stock-page]')) return;
    cancelInitial();
    const controller = initialController = new AbortController();
    grid.inert = true;
    grid.setAttribute('aria-busy', 'true');
    try {
      await reorder(document, location.href, { signal: controller.signal });
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
      if (typeof initBgimages === 'function') initBgimages();
      if (typeof initializeVideos === 'function') initializeVideos();
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Could not prioritize in-stock products:', error);
    } finally {
      grid.inert = false;
      grid.setAttribute('aria-busy', 'false');
    }
  }
  window.OBStockFirst = { reorder, init, cancelInitial };
})();
